import React from 'react'
import { Typography, Space, Button } from 'antd'
import { RightOutlined } from '@ant-design/icons'
import './preview.scss'
import { formatDate } from 'utils/dateUtils'
import Loader from 'helper/Loader'
import { FacebookIcon, InstagramIcon, LinkedInIcon, TikTokIcon, XIcon, YouTubeIcon } from '../platformIcons/Social_Icons'
import dummy from '../../../assets/Images/No-image-found.jpeg'
import { infoMessage } from 'api/global'

const { Title } = Typography

interface BlogPreviewProps {
  visible: boolean
  formData?: any
  onEdit?: () => void
  onSave?: () => void
  isSaving?: boolean
  onBackToBlog?: () => void
}

const BlogPreview: React.FC<BlogPreviewProps> = ({
  visible,
  formData,
  onEdit,
  onSave,
  isSaving,
  onBackToBlog
}) => {
  const handleBlogClick = () => {
    onBackToBlog?.()
  }

  // Validate that formData has required fields before showing preview
  const isValidPreviewData = () => {
    return formData &&
      formData?.title ||
      formData?.content ||
      formData?.author ||
      formData?.organization
  }

  // Validate required fields before saving
  const validateAndSave = () => {
    if (!formData?.title || formData.title.trim().length === 0) {
      infoMessage('Title is required')
      return
    }

    if (!formData?.content || formData.content.trim().length < 20) {
      infoMessage('Content must be at least 20 characters long')
      return
    }

    if (!formData?.author) {
      infoMessage('Please select an author')
      return
    }

    if (!formData?.organization) {
      infoMessage('Please select an organization')
      return
    }
    onSave?.()
  }

  const processContent = (html: string) => {
    if (!html) return '';
    return html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #2BA7CA; ">$1</a>'
    );
  };


  if (!visible || !isValidPreviewData()) return null

  return (
    <div className="blog-preview-fullpage">
      {isSaving && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}>
          <Loader />
        </div>
      )}
      <div className="blog-preview-container">
        <div className="blog-header">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                marginTop: '14px',
                gap: '8px'
              }}>
                <span
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                  onClick={handleBlogClick}
                >
                  Blog
                </span> <RightOutlined /> Blog Preview
              </div>

              <Title level={2} style={{ margin: 0, fontWeight: '600' }}>
                Preview
              </Title>
            </div>

            <Space>
              <Button
                style={{
                  width: '109px',
                  height: '37px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  border: "1px solid #2BA7CA",
                  color: "#2BA7CA"
                }}
                onClick={() => onEdit?.()}
              >
                Edit
              </Button>

              <Button
                type="primary"
                style={{
                  width: '84px',
                  height: '37px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  backgroundColor: '#2BA7CA'
                }}
                onClick={validateAndSave}
              >
                Save
              </Button>
            </Space>
          </div>

          {/* Header with Title and Featured Image */}
          <div className="preview-header-layout">
            {/* Left Side - Blog Details */}
            <div className="blog-details-left">
              <Title level={1} className="blog-title">
                {formData?.title}
              </Title>

              <div className="blog-meta">
                {formData?.author && (
                  <Space size="large">
                    <span className="meta-item">
                      <span style={{ color: "#616060", fontWeight: 400, fontSize: '16px' }}>Written by</span> &nbsp;  {formData?.author}
                    </span>
                  </Space>
                )}
                <div className='date-preview'>
                  <span className="meta-date">
                    {formatDate(formData?.publishDate)}
                  </span>
                  {/* <span className="meta-date">
                    {formData?.readTime} min read
                  </span> */}
                </div>
              </div>

              {/* Social Media Share */}
              {formData?.sharingOptions && Object.values(formData.sharingOptions).some(val => val === true) && (
                <div className="social-share-section">
                  <div style={{ fontWeight: '700', fontSize: '20px' }} className='platforms-icons'>
                    Share This Post
                  </div>

                  <Space size="middle">
                    {formData?.sharingOptions?.facebook &&
                      <div className="icon facebook">
                        <FacebookIcon />
                      </div>}

                    {formData?.sharingOptions?.instagram &&
                      <div className="icon instagram" style={{ cursor: 'pointer' }}>
                        <InstagramIcon />
                      </div>}
                    {formData?.sharingOptions?.tiktok &&
                      <div className="icon tiktok" style={{ cursor: 'pointer' }}>
                        <TikTokIcon />
                      </div>}
                    {formData?.sharingOptions?.X &&
                      <div className="icon x" style={{ cursor: 'pointer' }}>
                        <XIcon />
                      </div>}
                    {formData?.sharingOptions?.youtube && <div className="icon youtube" style={{ cursor: 'pointer' }}>
                      <YouTubeIcon />
                    </div>}

                    {formData?.sharingOptions?.linkedIn && <div className="icon linkedin" style={{ cursor: 'pointer' }}>
                      <LinkedInIcon />
                    </div>}
                  </Space>
                </div>
              )}
            </div>

            {/* Right Side - Featured Image */}
            <div className="featured-image-right">
              {formData?.featuredImage && (
                <div className="featured-image-container">
                  <img
                    src={formData?.featuredImage?.original ? formData?.featuredImage?.original : dummy}
                    alt="Featured"
                  />
                </div>
              )}
            </div>
          </div>
        </div>



        {/* Excerpt
        <Card className="excerpt-card-preview">
          <Paragraph className="excerpt-text">
            {formData?.excerpt}
          </Paragraph>
        </Card> */}

        {/* Blog Content */}
        {formData?.content && (
          <div className="blog-content">
            <div
              dangerouslySetInnerHTML={{ __html: processContent(formData?.content) }}
              className="content-body"
            />
          </div>
        )}

        {/* Bottom Author Section */}

        {formData?.author && (
          <div className="bottom-author-section">
            <div className="author-info">
              <div className="author-avatar">
                {formData?.authorAvatar ? (
                  <img src={formData?.authorAvatar} alt="Author" />
                ) : (
                  <div className="default-avatar">
                    {formData?.author?.charAt(0) || 'A'}
                  </div>
                )}
              </div>
              <div className="author-details">
                <div className="author-name">{formData?.author}</div>
              </div>
            </div>
            <Space size="middle">
              {formData?.sharingOptions?.facebook &&
                <div className="icon facebook">
                  <FacebookIcon />
                </div>}

              {formData?.sharingOptions?.instagram &&
                <div className="icon instagram" style={{ cursor: 'pointer' }}>
                  <InstagramIcon />
                </div>}
              {formData?.sharingOptions?.tiktok &&
                <div className="icon tiktok" style={{ cursor: 'pointer' }}>
                  <TikTokIcon />
                </div>}


              {formData?.sharingOptions?.X &&
                <div className="icon x" style={{ cursor: 'pointer' }}>
                  <XIcon />
                </div>}
              {formData?.sharingOptions?.youtube && <div className="icon youtube" style={{ cursor: 'pointer' }}>
                <YouTubeIcon />
              </div>}

              {formData?.sharingOptions?.linkedIn && <div className="icon linkedin" style={{ cursor: 'pointer' }}>
                <LinkedInIcon />
              </div>}
            </Space>
          </div>
        )}
      </div>
    </div>

  )
}

export default BlogPreview
